import type { SearchInput, SearchResultFragment } from '#graphql-operations'

interface PageData {
  items: SearchResultFragment[]
  pageNumber: number
}

interface CacheEntry {
  pages: Map<number, PageData>
  total: number
  timestamp: number
  itemsPerPage: number
}

const CACHE_TTL = 10 * 60 * 1000 // 10 minutes
const MAX_CACHE_ENTRIES = 20

export const useSearchCacheStore = defineStore('searchCache', () => {
  const cache = shallowRef(new Map<string, CacheEntry>())

  function generateCacheKey(searchInput: SearchInput): string {
    // Create a consistent key from search parameters
    // Exclude pagination params (skip, take) from the key
    const keyParams = {
      collectionId: searchInput.collectionId,
      collectionSlug: searchInput.collectionSlug,
      facetValueFilters: searchInput.facetValueFilters,
      groupByColors: searchInput.groupByColors,
      groupByProduct: searchInput.groupByProduct,
      inStock: searchInput.inStock,
      popularityScore: searchInput.popularityScore,
      priceRange: searchInput.priceRange,
      priceRangeWithTax: searchInput.priceRangeWithTax,
      sort: searchInput.sort,
      term: searchInput.term,
    }

    // Sort object keys for consistent serialization
    return JSON.stringify(keyParams, Object.keys(keyParams).sort())
  }

  function isExpired(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp > CACHE_TTL
  }

  function enforceMaxCacheSize(): void {
    if (cache.value.size >= MAX_CACHE_ENTRIES) {
      // Remove oldest entry
      const oldestKey = Array.from(cache.value.entries())
        .sort(([, a], [, b]) => a.timestamp - b.timestamp)[0]?.[0]

      if (oldestKey) {
        const newCache = new Map(cache.value)
        newCache.delete(oldestKey)
        cache.value = newCache
      }
    }
  }

  function setPageCache(
    searchInput: SearchInput,
    pageNumber: number,
    items: SearchResultFragment[],
    total: number,
    itemsPerPage: number,
  ): void {
    // Defer cache update to prevent blocking
    nextTick(() => {
      const key = generateCacheKey(searchInput)
      const newCache = new Map(cache.value)
      let entry = newCache.get(key)

      if (!entry) {
        enforceMaxCacheSize()
        entry = {
          pages: new Map(),
          total,
          timestamp: Date.now(),
          itemsPerPage,
        }
        newCache.set(key, entry)
      }

      // Set or update the page data
      entry.pages.set(pageNumber, {
        items: [...items], // Create a copy to avoid mutations
        pageNumber,
      })
      entry.total = total
      entry.timestamp = Date.now()

      // Trigger reactivity
      cache.value = newCache
    })
  }

  function getPageFromCache(searchInput: SearchInput, pageNumber: number): PageData | null {
    const key = generateCacheKey(searchInput)
    const entry = cache.value.get(key)

    if (!entry || isExpired(entry)) {
      if (entry && isExpired(entry)) {
        cache.value.delete(key)
      }
      return null
    }

    return entry.pages.get(pageNumber) || null
  }

  function getCacheEntry(searchInput: SearchInput): CacheEntry | null {
    const key = generateCacheKey(searchInput)
    const entry = cache.value.get(key)

    if (!entry || isExpired(entry)) {
      if (entry && isExpired(entry)) {
        cache.value.delete(key)
      }
      return null
    }

    return entry
  }

  function getAllCachedPages(searchInput: SearchInput): SearchResultFragment[] {
    const entry = getCacheEntry(searchInput)
    if (!entry)
      return []

    // Get all pages sorted by page number
    const sortedPages = Array.from(entry.pages.entries())
      .sort(([a], [b]) => a - b)
      .flatMap(([, pageData]) => pageData.items)

    return sortedPages
  }

  function getLoadedPageNumbers(searchInput: SearchInput): number[] {
    const entry = getCacheEntry(searchInput)
    if (!entry)
      return []

    return Array.from(entry.pages.keys()).sort((a, b) => a - b)
  }

  function clearCache(): void {
    cache.value = new Map()
  }

  function clearExpiredCache(): void {
    const newCache = new Map(cache.value)
    let hasChanges = false

    for (const [key, entry] of newCache.entries()) {
      if (isExpired(entry)) {
        newCache.delete(key)
        hasChanges = true
      }
    }

    if (hasChanges) {
      cache.value = newCache
    }
  }

  function removeCacheEntry(searchInput: SearchInput): void {
    const key = generateCacheKey(searchInput)
    const newCache = new Map(cache.value)
    newCache.delete(key)
    cache.value = newCache
  }

  // For backwards compatibility with existing code
  function getCache(searchInput: SearchInput): { items: SearchResultFragment[], loadedPages: number[], total: number, startPage: number, endPage: number } | null {
    const entry = getCacheEntry(searchInput)
    if (!entry)
      return null

    const items = getAllCachedPages(searchInput)
    const loadedPages = getLoadedPageNumbers(searchInput)

    if (items.length === 0)
      return null

    return {
      items,
      loadedPages,
      total: entry.total,
      startPage: Math.min(...loadedPages),
      endPage: Math.max(...loadedPages),
    }
  }

  return {
    cache,
    generateCacheKey,
    setPageCache,
    getPageFromCache,
    getCacheEntry,
    getAllCachedPages,
    getLoadedPageNumbers,
    clearCache,
    clearExpiredCache,
    removeCacheEntry,
    getCache, // For backwards compatibility
  }
})
